import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../App';
import { useI18n } from '../i18n/index.jsx';
import api from '../api';

/**
 * Client Onboarding Flow — 4-step registration
 * 
 * Step 0: Welcome screen
 * Step 1: Personal data form
 * Step 2: Contract type selection + terms
 * Step 3: Payment
 * Step 4: Complete — access granted
 */

export default function ClientOnboarding() {
  const { user, setUser, setPage } = useContext(AppContext);
  const { t } = useI18n();
  
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentDone, setPaymentDone] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null | 'awaiting_transfer' | 'pending_verification' | 'confirmed'
  const [companyDetails, setCompanyDetails] = useState(null);
  const [transferDetails, setTransferDetails] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const canTrial = user && !user.has_paid_access && !user.trial_used;

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    fontSize: 13,
    fontWeight: 600,
  };

  // Step 1 form data
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    pesel: '',
    date_of_birth: '',
    citizenship: '',
    passport_number: '',
    address_street: '',
    address_city: '',
    address_zip: '',
  });

  // Step 2 data
  const [contractType, setContractType] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  // Load existing onboarding status
  useEffect(() => {
    loadStatus();
  }, []);

  async function loadStatus() {
    try {
      const res = await api.get('/onboarding/status');
      const data = res.data;
      
      // Resume from saved step (onboarding_step = last completed step, show next)
      if (data.onboarding_step >= 4) {
        setStep(4);
        return;
      }
      if (data.onboarding_step > 0) {
        setStep(data.onboarding_step + 1);
      }

      // Pre-fill form with existing data
      setForm(prev => ({
        ...prev,
        first_name: data.first_name || prev.first_name,
        last_name: data.last_name || prev.last_name,
        phone: data.phone || prev.phone,
        email: data.email || prev.email,
        pesel: data.pesel || prev.pesel,
        date_of_birth: data.date_of_birth || prev.date_of_birth,
        citizenship: data.citizenship || prev.citizenship,
        passport_number: data.passport_number || prev.passport_number,
        address_street: data.address_street || prev.address_street,
        address_city: data.address_city || prev.address_city,
        address_zip: data.address_zip || prev.address_zip,
      }));

      if (data.has_paid_access) {
        setPaymentDone(true);
      }

      // If on step 3, load payment info
      if (data.onboarding_step >= 2 && data.onboarding_step < 4) {
        loadPaymentInfo();
      }
    } catch (e) {
      console.error('Failed to load onboarding status:', e);
    }
  }

  function updateForm(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  }

  function validateStep1() {
    const errors = {};
    const req = t('client_onboarding.required_field');
    
    if (!form.first_name.trim()) errors.first_name = req;
    if (!form.last_name.trim()) errors.last_name = req;
    if (!form.phone.trim()) errors.phone = req;
    if (!form.email.trim()) {
      errors.email = req;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      errors.email = t('client_onboarding.invalid_email');
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  // Step handlers
  async function handleStep1Submit() {
    if (!validateStep1()) return;
    
    setLoading(true);
    setError(null);
    try {
      const payload = { ...form };
      // Convert empty strings to null for optional fields
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      // Required fields must not be null
      payload.first_name = form.first_name.trim();
      payload.last_name = form.last_name.trim();
      payload.phone = form.phone.trim();
      payload.email = form.email.trim();
      
      await api.post('/onboarding/step1', payload);
      setStep(2);
    } catch (e) {
      setError(e.userMessage || e.response?.data?.detail || 'Ошибка');
    } finally {
      setLoading(false);
    }
  }

  async function handleStep2Submit() {
    if (!contractType) {
      setError('Выберите тип договора');
      return;
    }
    if (!termsAccepted) {
      setError(t('client_onboarding.terms_required'));
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await api.post('/onboarding/step2', {
        contract_type: contractType,
        accepted_terms: true,
      });
      setStep(3);
      loadPaymentInfo();
    } catch (e) {
      setError(e.userMessage || e.response?.data?.detail || 'Ошибка');
    } finally {
      setLoading(false);
    }
  }

  // Load payment status and company details when entering step 3
  async function loadPaymentInfo() {
    try {
      const [detailsRes, statusRes] = await Promise.all([
        api.get('/gateway-payments/company-details'),
        api.get('/gateway-payments/my/status').catch(() => ({ data: { status: 'none' } })),
      ]);
      setCompanyDetails(detailsRes.data);
      
      const s = statusRes.data;
      if (s.status === 'confirmed' || s.has_paid_access) {
        setPaymentStatus('confirmed');
        setPaymentDone(true);
      } else if (s.status === 'pending_verification' || s.status === 'pending') {
        setPaymentStatus('pending_verification');
      } else {
        setPaymentStatus('awaiting_transfer');
      }

      // Load transfer details
      try {
        const tdRes = await api.get('/gateway-payments/my/transfer-details');
        setTransferDetails(tdRes.data);
      } catch {}
    } catch (e) {
      setPaymentStatus('awaiting_transfer');
    }
  }

  async function handleCreateActivation() {
    setLoading(true);
    setError(null);
    try {
      await api.post('/gateway-payments/my/create-activation');
      await loadPaymentInfo();
    } catch (e) {
      // May already exist
      await loadPaymentInfo();
    } finally {
      setLoading(false);
    }
  }

  async function startCardPayment() {
    setCardLoading(true);
    try {
      const { data } = await api.post('/payments/contract/checkout', {});
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError('Не удалось создать Stripe сессию');
      }
    } catch (e) {
      setError(e.userMessage || e.response?.data?.detail || 'Ошибка Stripe');
    } finally {
      setCardLoading(false);
    }
  }


  async function checkPaymentConfirmed() {
    try {
      const statusRes = await api.get('/gateway-payments/my/status');
      const s = statusRes.data;
      if (s.status === 'confirmed' || s.has_paid_access) {
        setPaymentStatus('confirmed');
        setPaymentDone(true);
        // Complete onboarding
        await api.post('/onboarding/step3', { payment_confirmed: true });
        await api.post('/onboarding/complete');
        setStep(4);
        if (setUser) {
          const meRes = await api.get('/me');
          setUser(meRes.data);
        }
      }
    } catch {}
  }

  // Poll payment status every 10s when waiting for admin confirmation
  useEffect(() => {
    if (paymentStatus === 'pending_verification') {
      const interval = setInterval(checkPaymentConfirmed, 10000);
      return () => clearInterval(interval);
    }
  }, [paymentStatus]);

  function handleGoToDashboard() {
    setPage('dashboard');
  }

  // Render helper for form fields
  function renderField(name, label, type = 'text', required = false) {
    return (
      <div className="field" key={name}>
        <label>{label} {required && <span style={{ color: 'var(--accent)' }}>*</span>}</label>
        <input
          type={type}
          value={form[name] || ''}
          onChange={e => updateForm(name, e.target.value)}
          placeholder={label}
          style={fieldErrors[name] ? { borderColor: 'var(--danger)' } : {}}
        />
        {fieldErrors[name] && (
          <span style={{ color: 'var(--danger)', fontSize: '11px', marginTop: '2px' }}>
            {fieldErrors[name]}
          </span>
        )}
      </div>
    );
  }

  const progressPercent = Math.min(step, 4) / 4 * 100;

  return (
    <div className="onboarding-flow">
      {/* Progress bar */}
      {step > 0 && step < 4 && (
        <div className="co-progress">
          <div className="co-progress-bar">
            <div className="co-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="co-progress-text">
            {t('client_onboarding.step')} {Math.min(step, 4)}/4
          </div>
        </div>
      )}

      {error && (
        <div className="banner banner-warning" style={{ margin: '12px 0' }}>
          ⚠️ {error}
        </div>
      )}

      {/* ══════════ Step 0: Welcome ══════════ */}
      {step === 0 && (
        <div className="onboarding-step">
          <div className="model-icon">🚀</div>
          <h1>{t('client_onboarding.welcome_title')}</h1>
          <p className="model-desc">{t('client_onboarding.welcome_subtitle')}</p>
          
          <div className="model-features">
            <div className="feature">
              <span className="feature-icon">📋</span>
              <span>{t('client_onboarding.personal_data')}</span>
            </div>
            <div className="feature">
              <span className="feature-icon">📄</span>
              <span>{t('client_onboarding.contract_selection')}</span>
            </div>
            <div className="feature">
              <span className="feature-icon">💳</span>
              <span>{t('client_onboarding.payment_title')}</span>
            </div>
          </div>

          <button className="btn btn-primary btn-large" onClick={() => setStep(1)}>
            {t('client_onboarding.start_btn')} →
          </button>

          <button 
            className="btn btn-secondary btn-large" 
            style={{ marginTop: 12, width: '100%', opacity: 0.9 }}
            onClick={() => setPage('demo')}
          >
            🎮 Попробовать демо-режим (бесплатно)
          </button>
        </div>
      )}

      {/* ══════════ Step 1: Personal Data ══════════ */}
      {step === 1 && (
        <div className="onboarding-step" style={{ textAlign: 'left', alignItems: 'stretch' }}>
          <h1 style={{ textAlign: 'center' }}>📋 {t('client_onboarding.personal_data')}</h1>
          
          <div className="form">
            {renderField('first_name', t('client_onboarding.first_name'), 'text', true)}
            {renderField('last_name', t('client_onboarding.last_name'), 'text', true)}
            {renderField('phone', t('client_onboarding.phone'), 'tel', true)}
            {renderField('email', t('client_onboarding.email'), 'email', true)}
            
            <div className="co-section-divider" />
            
            {renderField('pesel', t('client_onboarding.pesel'), 'text')}
            {renderField('date_of_birth', t('client_onboarding.date_of_birth'), 'date')}
            {renderField('citizenship', t('client_onboarding.citizenship'), 'text')}
            {renderField('passport_number', t('client_onboarding.passport_number'), 'text')}
            
            <div className="co-section-divider" />
            
            {renderField('address_street', t('client_onboarding.address_street'), 'text')}
            {renderField('address_city', t('client_onboarding.address_city'), 'text')}
            {renderField('address_zip', t('client_onboarding.address_zip'), 'text')}
          </div>

          <button 
            className="btn btn-primary btn-large" 
            style={{ width: '100%', marginTop: '16px' }}
            onClick={handleStep1Submit}
            disabled={loading}
          >
            {loading ? '⏳...' : `${t('common.next')} →`}
          </button>
        </div>
      )}

      {/* ══════════ Step 2: Contract Selection ══════════ */}
      {step === 2 && (
        <div className="onboarding-step">
          <h1>📄 {t('client_onboarding.contract_selection')}</h1>
          
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ 
              marginBottom: 16, padding: 14, 
              background: 'rgba(59,130,246,0.1)', borderRadius: 12,
              fontSize: 13, textAlign: 'center', border: '1px solid rgba(59,130,246,0.2)'
            }}>
              💡 Рабочий контракт для легализации можно выбрать позже.<br/>
              Сейчас выберите тип или продолжите только с Gateway.
            </div>

            <div 
              style={{ padding: 16, borderRadius: 14, border: contractType === 'umowa_zlecenie' ? '2px solid #6c5ce7' : '2px solid rgba(255,255,255,0.1)', background: contractType === 'umowa_zlecenie' ? 'rgba(108,92,231,0.2)' : 'rgba(255,255,255,0.05)', cursor: 'pointer', textAlign: 'left' }}
              onClick={() => setContractType('umowa_zlecenie')}
            >
              <div className="type-icon">📋</div>
              <h3 style={{ fontSize: 18 }}>UMOWA ZLECENIE</h3>
              <p style={{ fontSize: 13, opacity: 0.9 }}>
                <b>Договор поручения</b> — самый популярный для иностранцев.<br/>
                ✅ Даёт легализацию — основание для карты побыту<br/>
                ✅ Гибкий график, нет фиксированных часов<br/>
                <span style={{ marginTop: 6, display: 'block' }}>
                  💰 <b>Налоги (платит клиент — обе стороны):</b><br/>
                  • ZUS работника: ~13.71% от брутто<br/>
                  • ZUS работодателя: ~19.48% от брутто<br/>
                  • PIT: 12% (до 120 000 PLN/год), 32% выше<br/>
                  • <b>Итого: ~45% от брутто</b>
                </span>
              </p>
            </div>

            <div 
              style={{ padding: 16, borderRadius: 14, border: contractType === 'umowa_o_dzielo' ? '2px solid #6c5ce7' : '2px solid rgba(255,255,255,0.1)', background: contractType === 'umowa_o_dzielo' ? 'rgba(108,92,231,0.2)' : 'rgba(255,255,255,0.05)', cursor: 'pointer', textAlign: 'left' }}
              onClick={() => setContractType('umowa_o_dzielo')}
            >
              <div className="type-icon">🎨</div>
              <h3 style={{ fontSize: 18 }}>UMOWA O DZIEŁO</h3>
              <p style={{ fontSize: 13, opacity: 0.9 }}>
                <b>Договор подряда</b> — для конкретного результата/проекта.<br/>
                ✅ Даёт легализацию — основание для карты побыту<br/>
                ✅ Идеально для разовых проектов, IT, дизайна, творчества<br/>
                <span style={{ marginTop: 6, display: 'block' }}>
                  💰 <b>Налоги (платит клиент):</b><br/>
                  • <b style={{ color: '#10b981' }}>ZUS: 0 PLN — не платится!</b><br/>
                  • PIT: 12% (с учётом 50% авторских вычетов — эффективно ~6%)<br/>
                  • <b>Итого: только PIT ~6-12% от брутто</b><br/>
                  • 💡 Самый выгодный вариант по налогам
                </span>
              </p>
            </div>

            <div 
              style={{ padding: 16, borderRadius: 14, border: contractType === 'kontrakt_menedzerski' ? '2px solid #6c5ce7' : '2px solid rgba(255,255,255,0.1)', background: contractType === 'kontrakt_menedzerski' ? 'rgba(108,92,231,0.2)' : 'rgba(255,255,255,0.05)', cursor: 'pointer', textAlign: 'left' }}
              onClick={() => setContractType('kontrakt_menedzerski')}
            >
              <div className="type-icon">💼</div>
              <h3 style={{ fontSize: 18 }}>KONTRAKT MENEDŻERSKI</h3>
              <p style={{ fontSize: 13, opacity: 0.9 }}>
                <b>Менеджерский контракт</b> — управление проектами/командой.<br/>
                ✅ Даёт легализацию — основание для карты побыту<br/>
                ✅ Высокий статус, подходит для руководителей и директоров<br/>
                <span style={{ marginTop: 6, display: 'block' }}>
                  💰 <b>Налоги (платит клиент — обе стороны):</b><br/>
                  • ZUS работника: ~13.71% от брутто<br/>
                  • ZUS работодателя: ~19.48% от брутто<br/>
                  • PIT: 12% (до 120 000 PLN/год), 32% выше<br/>
                  • <b>Итого: ~45% от брутто</b>
                </span>
              </p>
            </div>

            <div 
              style={{ padding: 16, borderRadius: 14, border: contractType === 'gateway_only' ? '2px solid #6c5ce7' : '2px dashed rgba(255,255,255,0.2)', background: contractType === 'gateway_only' ? 'rgba(108,92,231,0.2)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left' }}
              onClick={() => setContractType('gateway_only')}
            >
              <div className="type-icon">⏩</div>
              <h3 style={{ fontSize: 16 }}>ВЫБРАТЬ ПОЗЖЕ / БЕЗ ЛЕГАЛИЗАЦИИ</h3>
              <p style={{ fontSize: 13, opacity: 0.8 }}>
                Продолжить только с Gateway-договором.<br/>
                Рабочий контракт для легализации можно оформить позже в личном кабинете.
              </p>
            </div>
          </div>

          {/* Pricing — big and clear */}
          <div style={{ 
            marginTop: 20, padding: 20, 
            background: 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(59,130,246,0.2))', 
            borderRadius: 16, border: '2px solid rgba(99,102,241,0.4)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4 }}>ЕДИНОРАЗОВАЯ АКТИВАЦИЯ</div>
            <div style={{ fontSize: 36, fontWeight: 800, color: '#818cf8' }}>1 200 PLN</div>
            
            <div style={{ 
              height: 1, background: 'rgba(255,255,255,0.1)', 
              margin: '16px 0' 
            }} />
            
            <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4 }}>СО 2-ГО МЕСЯЦА — АБОНЕМЕНТ</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#60a5fa' }}>350 PLN / мес</div>
            
            <div style={{ 
              height: 1, background: 'rgba(255,255,255,0.1)', 
              margin: '16px 0' 
            }} />
            
            <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 4 }}>КОМИССИЯ НА ВЫВОД СРЕДСТВ</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>6%</div>
          </div>

          {/* Terms card */}
          <div className="co-terms-card" style={{ marginTop: '20px' }}>
            <div className="co-terms-header">📜 Я принимаю условия сотрудничества</div>

            <label className="checkbox-item" onClick={() => setTermsAccepted(!termsAccepted)}>
              <input 
                type="checkbox" 
                checked={termsAccepted} 
                onChange={e => setTermsAccepted(e.target.checked)}
              />
              <span>{t('client_onboarding.accept_terms')}</span>
            </label>
          </div>

          <button 
            className="btn btn-primary btn-large"
            style={{ width: '100%', marginTop: '16px' }}
            onClick={handleStep2Submit}
            disabled={loading || !termsAccepted || !contractType}
          >
            {loading ? '⏳...' : `✍️ ${t('client_onboarding.sign_continue')}`}
          </button>

          <button className="btn btn-link back-btn" onClick={() => setStep(1)}>
            ← {t('common.back')}
          </button>
        </div>
      )}

      {/* ══════════ Step 3: Payment ══════════ */}
      {step === 3 && (
        <div className="onboarding-step" style={{ textAlign: 'left', alignItems: 'stretch' }}>
          <h1 style={{ textAlign: 'center' }}>💳 {t('client_onboarding.payment_title')}</h1>
          
          {/* === Awaiting Transfer === */}
          {paymentStatus === 'awaiting_transfer' && (
            <>
              <div className="co-payment-card" style={{ marginTop: 16 }}>
                <div className="co-payment-label">{t('client_onboarding.payment_amount')}</div>
                <div className="co-payment-amount" style={{ fontSize: 28, fontWeight: 700 }}>
                  1 200 PLN
                </div>
                <div className="co-payment-note" style={{ marginTop: 8, fontSize: 13, opacity: 0.8 }}>
                  Активация Gateway — разовый платёж
                </div>
              </div>

              <h3 style={{ marginTop: 20 }}>💳 Apple Pay / Карта / BLIK (Stripe):</h3>
              <div style={{ background: 'rgba(16,185,129,0.1)', borderRadius: 10, padding: 12, border: '1px solid rgba(16,185,129,0.3)' }}>
                ✅ Онбординг 1200 PLN + подписка 350 PLN/мес (первое списание через 30 дней)
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <span style={badgeStyle}> Apple Pay</span>
                <span style={badgeStyle}>💳 Карта</span>
                <span style={badgeStyle}>🇵🇱 BLIK</span>
              </div>
              <button
                className="btn btn-primary btn-large"
                style={{ width: '100%', marginTop: 12 }}
                onClick={startCardPayment}
                disabled={cardLoading}
              >
                {cardLoading ? '⏳ Создаём оплату...' : 'Оплатить (Apple Pay / Карта / BLIK)'}
              </button>

              <h3 style={{ marginTop: 20 }}>🏦 Реквизиты для перевода:</h3>
              {companyDetails && (
                <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16, fontSize: 14, lineHeight: 1.8 }}>
                  <div><strong>Получатель:</strong> {companyDetails.name}</div>
                  <div><strong>IBAN:</strong> <span style={{ fontFamily: 'monospace' }}>{companyDetails.iban}</span></div>
                  <div><strong>Банк:</strong> {companyDetails.bank}</div>
                  <div><strong>NIP:</strong> {companyDetails.nip}</div>
                  <div><strong>Назначение:</strong> Aktywacja Gateway — {form.first_name} {form.last_name}</div>
                </div>
              )}

              <h3 style={{ marginTop: 20 }}>🏦 Метод оплаты:</h3>
              <div style={{ background: 'rgba(59,130,246,0.1)', borderRadius: 10, padding: 12, border: '1px solid rgba(59,130,246,0.3)' }}>
                🏦 <strong>Банковский перевод</strong> — на реквизиты выше
              </div>

              <div style={{ marginTop: 20, padding: 16, background: 'rgba(234,179,8,0.1)', borderRadius: 12, border: '1px solid rgba(234,179,8,0.3)', fontSize: 13 }}>
                ⚠️ После перевода нажмите "Я оплатил" — мы проверим поступление и откроем доступ.
              </div>

              <button 
                className="btn btn-primary btn-large"
                style={{ width: '100%', marginTop: 20 }}
                onClick={handleCreateActivation}
                disabled={loading}
              >
                {loading ? '⏳...' : '✅ Я оплатил — подтвердить'}
              </button>

              <button className="btn btn-link back-btn" onClick={() => setStep(2)}>
                ← {t('common.back')}
              </button>
            </>
          )}

          {/* === Pending Verification === */}
          {paymentStatus === 'pending_verification' && (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <div style={{ fontSize: 64 }}>⏳</div>
              <h2>Оплата на проверке</h2>
              <p style={{ opacity: 0.7, marginTop: 8 }}>
                Мы проверяем поступление средств. Обычно это занимает от нескольких минут до 24 часов.
              </p>
              <p style={{ opacity: 0.5, fontSize: 13, marginTop: 16 }}>
                Страница обновится автоматически когда оплата будет подтверждена.
              </p>
              <div className="spinner" style={{ margin: '20px auto' }} />
              
              <button 
                className="btn btn-secondary"
                style={{ marginTop: 16 }}
                onClick={checkPaymentConfirmed}
              >
                🔄 Проверить статус
              </button>
            </div>
          )}

          {/* === Confirmed === */}
          {paymentStatus === 'confirmed' && (
            <div className="co-payment-success" style={{ textAlign: 'center' }}>
              <div className="success-icon">✅</div>
              <p>{t('client_onboarding.payment_success')}</p>
              <div className="spinner" style={{ margin: '16px auto' }} />
            </div>
          )}
        </div>
      )}

      {/* ══════════ Step 4: Complete ══════════ */}
      {step === 4 && (
        <div className="onboarding-step success">
          <div className="success-icon">🎉</div>
          <h1>{t('client_onboarding.complete_title')}</h1>
          <p className="model-desc">{t('client_onboarding.complete_subtitle')}</p>

          <button 
            className="btn btn-primary btn-large"
            onClick={handleGoToDashboard}
          >
            {t('client_onboarding.go_dashboard')} →
          </button>
        </div>
      )}
    </div>
  );
}
